import { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Activity,
  Wifi,
  WifiOff,
  ZoomIn,
  ZoomOut,
  Layers,
  Sparkles,
  Maximize2,
  Crosshair,
  RotateCcw,
} from "lucide-react";
import CandlestickSurface from "@/components/chart/CandlestickSurface";
import {
  TIMEFRAMES,
  buildPositionLevels,
  buildOrderLevels,
  clamp,
  displaySymbol,
  formatPrice,
  formatQty,
  mergeCandle,
  mergeCandles,
  normalizeKlines,
  normalizeSymbol,
} from "@/components/chartUtils";
import "@/components/ChartWorkspace.css";

const DEFAULT_OVERLAYS = {
  ema20: true,
  ema50: false,
  vwap: true,
  volume: true,
  levels: true,
  orders: true,
  autofitLevels: true,
};

const marketEndpoints = (accountType = "live") => {
  if (accountType === "testnet") {
    return {
      rest: "https://testnet.binancefuture.com/fapi/v1/klines",
      stream: "wss://stream.binancefuture.com/ws",
    };
  }
  return {
    rest: "https://fapi.binance.com/fapi/v1/klines",
    stream: "wss://fstream.binance.com/ws",
  };
};

export default function PositionChartModal({
  item,
  isOpen,
  onClose,
  onOpenFullChart,
  accountType = "live",
}) {
  const [timeframe, setTimeframe] = useState("1m");
  const [candles, setCandles] = useState([]);
  const [streamState, setStreamState] = useState("connecting");
  const [marketError, setMarketError] = useState("");
  const [visibleCount, setVisibleCount] = useState(85);
  const [panOffset, setPanOffset] = useState(0);
  const [overlays, setOverlays] = useState(DEFAULT_OVERLAYS);
  const [loadingTf, setLoadingTf] = useState(false);
  const [liveTicker, setLiveTicker] = useState(null);

  const rawSymbol = item?.symbol || "BTCUSDT";
  const marketSymbol = normalizeSymbol(rawSymbol);
  const cleanDisplay = displaySymbol(rawSymbol).replace(":USDT", "").replace("/USDT", "");
  const endpoints = useMemo(() => marketEndpoints(accountType), [accountType]);

  // Determine if item is a position or an open order
  const isPosition = Boolean(item && (item.entry_price != null || item.side === "long" || item.side === "short"));
  const isLong = isPosition && (item.side === "long" || item.side === "buy");
  const isShort = isPosition && (item.side === "short" || item.side === "sell");

  // Real-time dynamic mark price & live PnL calculations
  const entryPrice = Number(item?.entry_price || item?.entryPrice || 0);
  const currentMark = liveTicker?.price || Number(item?.mark_price || item?.markPrice || entryPrice);
  const qty = Number(item?.qty || 0);
  const leverage = Math.max(1, Number(item?.leverage || 1));

  const livePnl = useMemo(() => {
    if (!isPosition || !entryPrice || !currentMark || !qty) return Number(item?.unrealized_pnl || 0);
    return isLong ? (currentMark - entryPrice) * qty : (entryPrice - currentMark) * qty;
  }, [isPosition, entryPrice, currentMark, qty, isLong, item?.unrealized_pnl]);

  const livePnlPct = useMemo(() => {
    if (!isPosition || !entryPrice || !currentMark) return Number(item?.pnl_pct || 0);
    return isLong
      ? ((currentMark - entryPrice) / entryPrice) * 100 * leverage
      : ((entryPrice - currentMark) / entryPrice) * 100 * leverage;
  }, [isPosition, entryPrice, currentMark, leverage, isLong, item?.pnl_pct]);

  const isProfit = livePnl >= 0;

  // Dynamic position object updated with live real-time price & PnL
  const dynamicItem = useMemo(() => {
    if (!item) return null;
    return {
      ...item,
      mark_price: currentMark,
      markPrice: currentMark,
      unrealized_pnl: livePnl,
      pnl_pct: livePnlPct,
    };
  }, [item, currentMark, livePnl, livePnlPct]);

  // Dynamic Levels for CandlestickSurface
  const levels = useMemo(() => {
    if (!dynamicItem) return [];
    if (isPosition) {
      return buildPositionLevels(dynamicItem);
    }
    return buildOrderLevels([dynamicItem]);
  }, [dynamicItem, isPosition]);

  // Distances to SL and TP
  const stopLoss = Number(item?.stop_loss || 0);
  const takeProfit = Number(item?.take_profit || 0);
  const slDist = currentMark && stopLoss > 0
    ? (((stopLoss - currentMark) / currentMark) * 100).toFixed(2)
    : null;
  const tpDist = currentMark && takeProfit > 0
    ? (((takeProfit - currentMark) / currentMark) * 100).toFixed(2)
    : null;

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Load and stream candles
  useEffect(() => {
    if (!isOpen || !marketSymbol) return;

    let active = true;
    let socket = null;
    let reconnectTimer = null;
    let requestController = null;
    let marketFrame = null;
    let pendingCandle = null;
    let reconnectAttempt = 0;

    setPanOffset(0);
    setMarketError("");
    setStreamState("connecting");
    setLoadingTf(true);

    const loadCandles = async () => {
      requestController?.abort();
      requestController = new AbortController();
      try {
        let rawData = null;
        // 1. Try local server cache endpoint first
        try {
          const apiRes = await fetch(
            `/api/market/candles?symbol=${encodeURIComponent(rawSymbol)}&timeframe=${encodeURIComponent(timeframe)}&limit=300`,
            { cache: "no-store", signal: requestController.signal }
          );
          if (apiRes.ok) {
            const data = await apiRes.json();
            if (Array.isArray(data) && data.length >= 10) {
              rawData = data;
            }
          }
        } catch {
          // fallback below
        }

        // 2. Direct Binance REST fallback
        if (!rawData) {
          const params = new URLSearchParams({ symbol: marketSymbol, interval: timeframe, limit: "300" });
          const response = await fetch(`${endpoints.rest}?${params.toString()}`, {
            cache: "no-store",
            signal: requestController.signal,
          });
          if (!response.ok) throw new Error(`Market data request failed (${response.status})`);
          rawData = await response.json();
        }

        const normalized = normalizeKlines(rawData, 300);
        if (!normalized.length) throw new Error("No candles available for this symbol");
        if (!active) return;
        setCandles(normalized);
        const last = normalized[normalized.length - 1];
        if (last && last.close) {
          setLiveTicker({
            price: last.close,
            time: last.time,
            direction: "neutral",
            updatedAt: Date.now(),
          });
        }
        setMarketError("");
        setStreamState("live");
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        setMarketError(error?.message || "Unable to load market candles");
        setStreamState("offline");
      } finally {
        if (active) setLoadingTf(false);
      }
    };

    const flushPendingCandle = () => {
      marketFrame = null;
      if (!active || !pendingCandle) return;
      const candle = pendingCandle;
      pendingCandle = null;
      setCandles((current) => mergeCandle(current, candle, 300));
      setMarketError("");
      setStreamState("live");
    };

    const scheduleReconnect = () => {
      if (!active || reconnectTimer) return;
      reconnectAttempt += 1;
      const delay = Math.min(3000, 400 * reconnectAttempt);
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (!active) return;
      if (socket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.readyState)) return;
      setStreamState((cur) => (cur === "live" ? cur : "connecting"));

      try {
        const streamUrl = `${endpoints.stream}/${marketSymbol.toLowerCase()}@kline_${timeframe}`;
        socket = new WebSocket(streamUrl);
        socket.onopen = () => {
          if (!active) return;
          reconnectAttempt = 0;
          setStreamState("live");
        };
        socket.onmessage = (event) => {
          if (!active) return;
          try {
            const payload = JSON.parse(event.data);
            const data = payload?.data || payload;
            const kline = data?.k;
            if (!kline) return;

            const tickClose = Number(kline.c);
            if (tickClose && !isNaN(tickClose)) {
              setLiveTicker((prev) => ({
                price: tickClose,
                time: Number(kline.t),
                direction: prev?.price ? (tickClose >= prev.price ? "up" : "down") : "neutral",
                updatedAt: Date.now(),
              }));
            }

            pendingCandle = {
              time: Number(kline.t),
              open: Number(kline.o),
              high: Number(kline.h),
              low: Number(kline.l),
              close: tickClose,
              volume: Number(kline.v),
              closed: Boolean(kline.x),
            };
            if (!marketFrame) marketFrame = requestAnimationFrame(flushPendingCandle);
          } catch {
            // ignore malformed frame
          }
        };
        socket.onerror = () => {
          if (!active) return;
          setStreamState("offline");
        };
        socket.onclose = () => {
          if (!active) return;
          setStreamState("offline");
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    loadCandles();
    connect();

    return () => {
      active = false;
      requestController?.abort();
      if (marketFrame) cancelAnimationFrame(marketFrame);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
      }
    };
  }, [isOpen, marketSymbol, rawSymbol, timeframe, endpoints]);

  if (!isOpen || !item) return null;

  const handleOpenFull = () => {
    if (onOpenFullChart) {
      onOpenFullChart(rawSymbol);
    }
    onClose();
  };

  const handleZoom = (delta) => {
    setVisibleCount((cur) => clamp(cur + delta, 25, 240));
  };

  const handleResetZoom = () => {
    setVisibleCount(85);
    setPanOffset(0);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl w-full max-w-5xl h-[88vh] max-h-[760px] flex flex-col overflow-hidden text-zinc-100 font-mono relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="position-chart-modal"
      >
        {/* TOP BAR: Symbol Info & Key Actions */}
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/60 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            {/* Side Indicator / Icon */}
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center border shadow-lg ${
                isLong
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 shadow-emerald-950"
                  : isShort
                  ? "bg-rose-500/10 border-rose-500/30 text-rose-400 shadow-rose-950"
                  : "bg-sky-500/10 border-sky-500/30 text-sky-400 shadow-sky-950"
              }`}
            >
              {isLong ? <TrendingUp size={18} /> : isShort ? <TrendingDown size={18} /> : <Layers size={18} />}
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black tracking-wide text-zinc-100">{cleanDisplay}</h2>
                <span className="text-[10px] text-zinc-400 bg-zinc-900 px-1.5 py-0.5 rounded border border-zinc-800 font-bold">
                  USDT-M
                </span>
                {isPosition && (
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                      isLong
                        ? "bg-emerald-950/80 text-emerald-400 border-emerald-800/60"
                        : "bg-rose-950/80 text-rose-400 border-rose-800/60"
                    }`}
                  >
                    {item.side} {leverage > 1 ? `${leverage}x` : ""}
                  </span>
                )}
                {!isPosition && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-sky-950/80 text-sky-400 border border-sky-800/60">
                    {item.order_type || item.type || "ORDER"}
                  </span>
                )}
              </div>

              {/* Price & Real-time Live PnL summary row */}
              <div className="flex items-center gap-3 text-[11px] text-zinc-400 mt-0.5">
                {isPosition && entryPrice > 0 && (
                  <span>
                    Entry: <strong className="text-zinc-200">${formatPrice(entryPrice)}</strong>
                  </span>
                )}
                {isPosition && currentMark > 0 && (
                  <span className="inline-flex items-center gap-1">
                    Mark:
                    <strong
                      className={`transition-colors font-bold ${
                        liveTicker?.direction === "up"
                          ? "text-emerald-400"
                          : liveTicker?.direction === "down"
                          ? "text-rose-400"
                          : "text-zinc-100"
                      }`}
                    >
                      ${formatPrice(currentMark)}
                    </strong>
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        liveTicker?.direction === "up"
                          ? "bg-emerald-400 animate-pulse"
                          : liveTicker?.direction === "down"
                          ? "bg-rose-400 animate-pulse"
                          : "bg-zinc-500"
                      }`}
                    />
                  </span>
                )}
                {isPosition && (
                  <span className={`font-bold ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                    PnL: {isProfit ? "+" : ""}${Number(livePnl).toFixed(2)} ({isProfit ? "+" : ""}
                    {Number(livePnlPct).toFixed(2)}%)
                  </span>
                )}
                {!isPosition && (item.stop_price || item.price) && (
                  <span>
                    Trigger: <strong className="text-sky-400">${formatPrice(item.stop_price || item.price)}</strong>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right Controls: Timeframe, Workspace Button & Close */}
          <div className="flex items-center gap-2">
            {/* Timeframe selector with smooth transition */}
            <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-lg p-0.5">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-all relative ${
                    timeframe === tf
                      ? "bg-emerald-500/20 text-emerald-400 shadow-sm border border-emerald-500/40"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60"
                  }`}
                >
                  {tf}
                  {timeframe === tf && loadingTf && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                  )}
                </button>
              ))}
            </div>

            {/* Open Full Workspace button */}
            {onOpenFullChart && (
              <button
                onClick={handleOpenFull}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold transition-colors"
                title="Open in Full Chart Workspace"
              >
                <Maximize2 size={13} />
                <span className="hidden sm:inline">Open Full Workspace</span>
                <span className="sm:hidden">Full</span>
              </button>
            )}

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 border border-zinc-800 transition-colors"
              title="Close modal (Esc)"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* METRICS & LEVEL PILLS STRIP */}
        <div className="px-4 py-2 border-b border-zinc-800/80 bg-zinc-900/30 flex flex-wrap items-center justify-between gap-2 text-[11px] shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isPosition && stopLoss > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-950/40 border border-amber-800/50 text-amber-300">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                SL: <strong>${formatPrice(stopLoss)}</strong>
                {slDist && <span className="text-[10px] opacity-80">({Number(slDist) >= 0 ? "+" : ""}{slDist}%)</span>}
              </span>
            )}
            {isPosition && takeProfit > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-950/40 border border-emerald-800/50 text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                TP: <strong>${formatPrice(takeProfit)}</strong>
                {tpDist && <span className="text-[10px] opacity-80">({Number(tpDist) >= 0 ? "+" : ""}{tpDist}%)</span>}
              </span>
            )}
            {isPosition && item.margin != null && (
              <span className="text-zinc-400">
                Margin: <strong className="text-zinc-200">${Number(item.margin).toFixed(2)}</strong>
              </span>
            )}
            {qty > 0 && (
              <span className="text-zinc-400">
                Qty: <strong className="text-zinc-200">{formatQty(qty)}</strong>
              </span>
            )}
          </div>

          {/* Technical Overlay Toggles & Auto-Fit */}
          <div className="flex items-center gap-1.5">
            {/* Auto-Fit SL/TP Levels button */}
            <button
              onClick={() => setOverlays((c) => ({ ...c, autofitLevels: !c.autofitLevels }))}
              className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-colors flex items-center gap-1 ${
                overlays.autofitLevels
                  ? "bg-emerald-950/60 text-emerald-300 border-emerald-700/60"
                  : "bg-zinc-950 text-zinc-500 border-zinc-800 hover:text-zinc-300"
              }`}
              title="Auto-scale Y-axis to fit Entry, Stop Loss and Take Profit levels"
            >
              <Crosshair size={11} />
              Fit Levels
            </button>

            {[
              { id: "ema20", label: "EMA 20" },
              { id: "ema50", label: "EMA 50" },
              { id: "vwap", label: "VWAP" },
              { id: "volume", label: "VOL" },
              { id: "levels", label: "LEVELS" },
            ].map((ov) => (
              <button
                key={ov.id}
                onClick={() => setOverlays((c) => ({ ...c, [ov.id]: !c[ov.id] }))}
                className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-colors ${
                  overlays[ov.id]
                    ? "bg-zinc-800 text-zinc-100 border-zinc-700"
                    : "bg-zinc-950 text-zinc-500 border-zinc-800/80 hover:text-zinc-300"
                }`}
              >
                {ov.label}
              </button>
            ))}

            <div className="h-3 w-px bg-zinc-800 mx-1" />

            <button
              onClick={() => handleZoom(-15)}
              className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"
              title="Zoom in (+)"
            >
              <ZoomIn size={13} />
            </button>
            <button
              onClick={() => handleZoom(15)}
              className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"
              title="Zoom out (-)"
            >
              <ZoomOut size={13} />
            </button>
            <button
              onClick={handleResetZoom}
              className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"
              title="Reset Zoom & Pan"
            >
              <RotateCcw size={13} />
            </button>
          </div>
        </div>

        {/* CHART SURFACE AREA */}
        <div className="relative flex-1 min-h-[380px] w-full bg-[#070d14] overflow-hidden">
          {candles.length > 0 ? (
            <CandlestickSurface
              candles={candles}
              timeframe={timeframe}
              levels={levels}
              orders={!isPosition ? [item] : []}
              visibleCount={visibleCount}
              setVisibleCount={setVisibleCount}
              panOffset={panOffset}
              setPanOffset={setPanOffset}
              overlays={overlays}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-zinc-500">
              {marketError ? (
                <div className="text-center px-4">
                  <span className="text-rose-400 font-semibold">{marketError}</span>
                  <p className="text-xs text-zinc-600 mt-1">Check exchange connectivity or symbol availability.</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Activity size={24} className="text-emerald-400 animate-spin" />
                  <span className="text-xs">Streaming {cleanDisplay} candlestick data...</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER BAR: Stream Health & Real-time Telemetry */}
        <div className="px-4 py-2 border-t border-zinc-800/80 bg-zinc-900/50 flex flex-wrap items-center justify-between gap-2 text-[10px] text-zinc-400 shrink-0">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 font-bold ${
                streamState === "live" ? "text-emerald-400" : "text-amber-400"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  streamState === "live" ? "bg-emerald-400 animate-ping" : "bg-amber-400"
                }`}
              />
              {streamState === "live" ? "LIVE REAL-TIME FEED (WSS)" : "CONNECTING FEED..."}
            </span>
            <span className="text-zinc-600">|</span>
            <span>{candles.length} bars ({timeframe})</span>
            {liveTicker && (
              <>
                <span className="text-zinc-600">|</span>
                <span className="text-zinc-300">
                  Last Tick: <strong>${formatPrice(liveTicker.price)}</strong>
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-3 text-zinc-500">
            <span className="hidden sm:inline">Drag to pan • Scroll to zoom</span>
            <span className="text-zinc-600">•</span>
            <span>Press <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono text-[9px]">ESC</kbd> to close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
